import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import API_ENDPOINTS from '../config/api';
import { useInstanceId, useInstance } from '../contexts/InstanceContext';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import HomeworkView, { BEFORE_SECTIONS, AFTER_SECTIONS } from '../components/Homework/HomeworkView';

/**
 * /homework — the room's picture on the wall, before and after the Kuutio
 * (#693). Fetches one aggregate for the instance, refreshes it every minute,
 * and steps through the sections with the arrow keys or the on-screen
 * buttons. ?tab=after opens the after tab; the section index is not kept in
 * the URL, a wall does not refresh mid-talk.
 */
export default function HomeworkScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);
  const { currentInstance } = useInstance();
  useScreenNavigation();
  const tab = searchParams.get('tab') === 'after' ? 'after' : 'before';
  const [section, setSection] = useState(0);
  const [state, setState] = useState({ data: null, error: null });

  const load = useCallback(() => {
    if (!instanceId) return;
    fetch(API_ENDPOINTS.INSTANCE_HOMEWORK(instanceId), { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; })
      .then((d) => setState({ data: d, error: null }))
      .catch((err) => setState((prev) => ({ data: prev.data, error: err.message })));
  }, [instanceId]);
  useEffect(() => { load(); const id = setInterval(load, 60_000); return () => clearInterval(id); }, [load]);

  const setTab = (next) => { const p = new URLSearchParams(searchParams); if (next === 'after') p.set('tab', 'after'); else p.delete('tab'); setSearchParams(p); setSection(0); };
  const count = (tab === 'before' ? BEFORE_SECTIONS : AFTER_SECTIONS).length;
  const go = useCallback((delta) => setSection((s) => Math.max(0, Math.min(count - 1, s + delta))), [count]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
      else if (e.key === 'r') load();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, load]);

  if (state.error && !state.data) return <div style={{ padding: '3rem', color: '#FF8A85', background: '#0b0d12', minHeight: '100vh' }}>{state.error}</div>;
  return <HomeworkView data={state.data} title={currentInstance?.title || ''} tab={tab} section={section} onTab={setTab} onSection={setSection} />;
}
